import { el, awaitRedraw } from "@dhx/ts-common/dom";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import { Input } from "./input";
import { ITextAreaConfig, ITextArea, ItemEvent, ITextAreaProps, ITextAreaEventHandlersMap } from "../types";
import { isDefined, isEmptyObj } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";

export class Textarea extends Input implements ITextArea {
	public config: ITextAreaConfig & { type: any; validation: any };
	public events: IEventSystem<ItemEvent, ITextAreaEventHandlersMap>;

	protected _propsItem = [
		"required",
		"validation",
		"placeholder",
		"readOnly",
		"maxlength",
		"minlength",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	protected _props = [...baseProps, ...this._propsItem];

	setValue(value: string): void {
		if (typeof value === "undefined" || this.config.value === value) return;

		this.config.value = value;
		this.events.fire(ItemEvent.change, [this.getValue()]);
		isVerify(this.config) && this.validate();
	}
	getValue(): string {
		return String(this.config.value ?? "");
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.textarea.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.textarea.el.blur();
		});
	}
	setProperties(propertyConfig: ITextAreaProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): ITextAreaProps {
		const obj: ITextAreaProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	protected _initView(config: ITextAreaConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			required: false,
			validation: undefined,
			placeholder: "",
			readOnly: false,
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
		this.paint();
	}
	protected _draw() {
		const {
			id,
			value,
			disabled,
			name,
			placeholder,
			required,
			resizable,
			readOnly,
			maxlength,
			minlength,
			label,
			helpMessage,
			height,
		} = this.config;

		const activeFocus = this.getRootView()?.refs?.textarea?.el === document.activeElement;

		return el(
			"div.dhx_form-group.dhx_form-group--textarea",
			{
				class: getFormItemCss(this.config, isVerify(this.config), activeFocus),
			},
			[
				this._drawLabel(),
				el(".dhx_input__wrapper", [
					el("textarea.dhx_input.dhx_input--textarea", {
						type: "text",
						id,
						"data-dhx-id": name || id,
						placeholder: placeholder || "",
						value: isDefined(value) ? value : "",
						name: name || "",
						disabled,
						required,
						readOnly,
						maxlength,
						minlength,
						onblur: this._handlers.onblur,
						oninput: this._handlers.oninput,
						onchange: this._handlers.onchange,
						onfocus: this._handlers.onfocus,
						onkeydown: this._handlers.onkeydown,
						_hooks: {
							didInsert: node => {
								if (height === "content") {
									const textarea = node.el;
									textarea.style.height = textarea.scrollHeight + "px";
								}
							},
						},
						style: {
							resize: resizable ? "both" : "none",
						},
						_ref: "textarea",
						"aria-label": label || `${readOnly ? value : ""} type text`,
						"aria-describedby": helpMessage ? `dhx_label__help_${id || this._uid}` : null,
					}),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", {}, getValidationMessage(this.config)),
				]),
			]
		);
	}
}
